import React, {useEffect, useState} from "react";
import {Box, Button, Grid, Link, Paper} from "@material-ui/core"
import _ from "lodash";
import useFetchableData from "../../../../../../hooks/useFetchableData";
import RectangularSkeleton from "../../../../shared/Skeleton/RectangularSkeleton";
import useDataFetchers from "../../../../../../hooks/useDataFetchers";
import InputComboBox from "../../../../sharedFormComponents/InputComboBox";
import SingleAsyncAutoComplete from "../../../../shared/SingleAsyncAutoComplete";

const RelatedSection = (
    {
        isEditing, handleOnChange, handleOnChangeInField, setErrors,
        session, relationships, fetchRelationships,
        fetchKey = "relationships", title = "Related Entities",
        ...props
    }
) => {
    const [relatedErrors, setRelatedErrors] = useState([]);
    const {
        data: relatedEntities,
        setData: setRelatedEntities,
        initialFetch
    } = useFetchableData(fetchRelationships, fetchKey);

    useEffect(() => {
        handleOnChangeInField("relatable_firms_attributes", [...relatedEntities], initialFetch)
    }, [relatedEntities])

    const setRelatedError = (index, error) => {
        let newErrors = [...relatedErrors];
        newErrors[index] = error;
        setRelatedErrors(newErrors);
    }

    useEffect(() => {
        setErrors(relatedErrors.some((error) => error))
    }, [relatedErrors]);

    const handleAddRelatedEntity = () => {
        let newRelatedEntities = [...relatedEntities];

        newRelatedEntities.push({
            relationship_id: undefined,
            related_id: undefined,
            key: Date.now(),
        })

        setRelatedEntities(newRelatedEntities);
        setRelatedErrors(prevState => ([...prevState, true]))
    }

    return (
        <Grid container item xs={12}>
            <RectangularSkeleton loading={!initialFetch} width={1000} height={300}>
                <Paper className="form-container" elevation={3}>
                    <Grid item xs={12}>
                        <h3>{title}</h3>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Button color="primary" variant="contained" onClick={handleAddRelatedEntity}
                                    style={{marginBottom: '20px'}}>Add New Related Entity</Button>
                        </Grid>
                        {
                            relatedEntities.map((relatedEntity, index) =>
                                <RelatedEntity
                                    key={relatedEntity.related_id || relatedEntity.key}
                                    index={index}
                                    session={session}
                                    relatedEntities={relatedEntities}
                                    setRelatedEntities={setRelatedEntities}
                                    relatedEntityData={relatedEntity}
                                    relationships={relationships}
                                    handleOnError={(error) => setRelatedError(index, error)}
                                    isEditing={isEditing}
                                />
                            )
                        }
                    </Grid>
                </Paper>
            </RectangularSkeleton>
        </Grid>
    )
}

const RelatedEntity = (
    {
        index, session, relatedEntities,
        setRelatedEntities, relatedEntityData, relationships,
        handleOnError, isEditing, ...props
    }
) => {
    const [selectedFoundation, setSelectedFoundation] = useState({
        id: relatedEntityData?.related_id || '',
        name: relatedEntityData?.related_name || '',
    });
    const [relatedEntity, setRelatedEntity] = useState({...relatedEntityData});
    const [foundationError, setFoundationError] = useState(!isEditing);
    const fetchers = useDataFetchers(session);

    const requiredErrors = () => {
        return foundationError || _.isEmpty(relatedEntity.relationships)
    }

    useEffect(() => {
        handleOnError(requiredErrors());
    }, [
        foundationError, relatedEntity.relationships
    ]);

    useEffect(() => {
        let newRelatedEntities = [...relatedEntities];
        newRelatedEntities[index] = relatedEntity;
        setRelatedEntities(newRelatedEntities);
    }, [relatedEntity])

    const handleOnChange = (event) => {
        event.persist();

        setRelatedEntity(prevState => ({
            ...prevState,
            [event.target.name]: event.target.value,
        }));
    }

    const handleOnChangeAutocomplete = (field, selectedValue) => {
        let fieldData = {};

        switch (field) {
            case 'related_id':
                fieldData = {
                    related_id: selectedValue?.id,
                    related_name: selectedValue?.name,
                }

                setSelectedFoundation(selectedValue);
                break;
        }

        setRelatedEntity(prevState => ({
            ...prevState,
            ...fieldData,
        }));
    }

    const handleRemoveRelatedEntity = () => {
        if (window.confirm('Are you sure?')) {
            setRelatedEntity(prevState => ({
                ...prevState,
                _destroy: true,
            }));
            handleOnError(false);
        }
    }

    const errorMessage = {};

    const handleRelationshipsChange = (event) => {
        const currentRelationshipIds = relatedEntity?.relationships?.map(({relationship_id}) => relationship_id) || [];
        const keepRelationshipIds = event.target.value.filter((id) => currentRelationshipIds.includes(id));
        const newRelationshipIds = event.target.value.filter((id) => !currentRelationshipIds.includes(id));
        const newRelationships = relationships.filter((relationship) => newRelationshipIds.includes(relationship.id)).map((relationship) => ({relationship_id: relationship.id, relationship_name: relationship.name}));
        const keepRelationships = relatedEntity?.relationships?.filter((relationship) => keepRelationshipIds.includes(relationship.relationship_id)) || [];

        setRelatedEntity(prevState => ({
            ...prevState,
            [event.target.name]: [
                ...keepRelationships,
                ...newRelationships
            ],
        }));
    }

    return (
        relatedEntity._destroy === true ? null :
            <>
                <Grid item xs={4}>
                    <InputComboBox
                        value={relatedEntity?.relationships?.map(({relationship_id, relationship_name}) => (relationship_id)) || []}
                        label={'Relationship'}
                        field={'relationships'}
                        name={'relationships'}
                        handleOnChange={handleRelationshipsChange}
                        options={relationships}
                        menuClass={'input-combo-menu'}
                        error={_.isEmpty(relatedEntity?.relationships)}
                        required
                        multiple
                    />
                </Grid>

                <Grid item xs={4}>
                    <SingleAsyncAutoComplete
                        fetch={fetchers.allFirms}
                        handleOnChange={(value) => handleOnChangeAutocomplete('related_id', value)}
                        handleOnError={(error) => setFoundationError(error)}
                        value={selectedFoundation}
                        label={'Entity'}
                        field={'related_id'}
                        required
                    />
                    <span>{errorMessage['related_id']}</span>
                </Grid>

                <Grid item xs={2}>
                    <Box style={{display: 'flex', alignItems: 'center', height: '100%'}}>
                        <Link onClick={handleRemoveRelatedEntity}>Remove</Link>
                    </Box>
                </Grid>
            </>
    )
}

export default RelatedSection;